import { useEffect, useState } from 'react';

// Parse "Artist - Title" out of a file path/name as a fallback when we have no
// enriched metadata for the track. Paths may use either separator — imports
// store forward slashes but Windows-native flows produce backslashes.
export function parseArtistTitle(filePath) {
    const stem = (filePath || '').split(/[\\/]/).pop().replace(/\.[^/.]+$/, '');
    if (stem.includes(' - ')) {
        const p = stem.split(' - ');
        return { artist: p[0].trim(), title: p.slice(1).join(' - ').trim() };
    }
    return { artist: 'Unknown Artist', title: stem };
}

// Musical parentheticals we must NEVER strip — "(Original Mix)", "(Andhim
// Remix)", "[Extended Mix]" etc. carry real track info, unlike a label tag or a
// website credit.
const MUSICAL_PAREN_RE = /\b(?:re-?)?(?:remix|rmx|rework|edit|mix|dub|version|instrumental|acoustic|radio|extended|original|bootleg|vip|mashup|flip|refix|live|acapella|a[- ]?capella)\b/i;
// A bare domain/website like "CahulHouseMafia.Net" or "www.foo.com" — a couple
// of dot-separated label-safe chunks, optionally www-prefixed, no spaces.
const DOMAIN_RE = /^(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)+\/?$/i;
// Suffixes WE add when fixing/normalizing a track — "[fixed]", "[-9 LUFS]".
// Unlike a label/website tag, these are the ONLY thing distinguishing a fixed
// file from its untouched original, so we keep them in the title instead of
// stripping them (otherwise the pair renders with the identical name). Kept in
// sync with the same constant in backend/cleanTitle.js.
const PRESERVE_TAG_RE = /^\s*(?:fixed|-?\d+(?:\.\d+)?\s*LUFS)\s*$/i;

// Strip junk that DJ exports tack onto the END of a track title for display:
//   • a record-label tag in square brackets  — "… [CitySlang]"
//   • a website/domain credit in parentheses  — "… (CahulHouseMafia.Net)"
// Musical parentheticals are preserved. Only trailing groups are considered, and
// we peel repeatedly so "(Original Mix) (site.net)" → "(Original Mix)".
export function cleanDisplayTitle(title) {
    let t = (title || '').trim();
    // Loop-guard: at most a handful of trailing groups; avoids any pathological spin.
    for (let i = 0; i < 6 && t; i++) {
        const brk = t.match(/\s*\[([^\][]*)\]\s*$/);
        if (brk && !MUSICAL_PAREN_RE.test(brk[1]) && !PRESERVE_TAG_RE.test(brk[1])) { t = t.slice(0, brk.index).trim(); continue; }
        const par = t.match(/\s*\(([^()]*)\)\s*$/);
        if (par && DOMAIN_RE.test(par[1].trim())) { t = t.slice(0, par.index).trim(); continue; }
        break;
    }
    // Never blank the title out entirely — fall back to the original if the
    // cleanup consumed everything (e.g. a title that was only a bracket tag).
    return t || (title || '').trim();
}

// Resolve the best display title/artist/cover for a local file: prefer the
// enriched (Mixpedia / ID3) data we already stored, fall back to the filename.
// `label` is the ready-to-render row text — use it instead of composing
// `${artist} - ${title}` at call sites: titles imported from filenames often
// already embed the artist, and composing blindly renders it twice
// ("Club Calimari - Club Calimari - Can You Feel It").
export function getEnrichedParts(map, filePath) {
    const e = map && typeof map.get === 'function' ? map.get(filePath) : null;
    const parsed = parseArtistTitle(filePath);
    const title = cleanDisplayTitle(e?.title || parsed.title);
    const artist = e?.artist || parsed.artist;
    const label = (artist && artist !== 'Unknown Artist'
        && !title.toLowerCase().startsWith(`${artist.toLowerCase()} - `))
        ? `${artist} - ${title}`
        : title;
    return { title, artist, cover: e?.cover || '', djpalId: e?.djpalId || null, label };
}

// Canonical form for path comparison: one separator style, precomposed
// unicode, case-folded. macOS folder scans return decomposed (NFD) accents
// while imported libraries store precomposed (NFC) — visually identical
// strings that fail ===; both macOS and Windows filesystems are
// case-insensitive, so casing differences are also not real differences.
const normPath = (p) => (p || '').replace(/\\/g, '/').normalize('NFC').toLowerCase();

// Suffixes WE append when fixing/normalizing a file — "[fixed]", "[-9 LUFS]".
// A fixed copy is the same track as its original, so basenames and titles are
// compared with these tags stripped: that's what lets "X [-9 LUFS].wav" borrow
// the cover/artist/id enrichment of "X.wav". (Display still keeps the tag —
// see PRESERVE_TAG_RE above and the re-append in get().)
const FIX_TAG_SOURCE = '\\[\\s*(?:fixed|-?\\d+(?:\\.\\d+)?\\s*lufs)\\s*\\]';
const stripFixTags = (s) => (s || '').replace(new RegExp(`\\s*${FIX_TAG_SOURCE}`, 'gi'), '').trim();

// Build the lookup used by useEnrichedTracks. Exposed for tests. `.get()`
// resolves a file path to its enriched {title, artist, cover}:
//   1. by normalized full path
//   2. by basename — a copy of an analyzed track (external drive vs local
//      folder) still enriches. Two entries sharing a basename are treated as
//      the SAME track only when their titles agree (ignoring punctuation and
//      case) or one has no title; different tracks that happen to share a
//      filename ("track01.wav") stay on the filename fallback instead of
//      risking the wrong cover.
// A scan of a copied folder writes a bare DB row (no cover) for the new path.
// That row must not shadow its enriched twin — an exact-path hit without a
// cover still borrows the cover from the same-track basename match.
export function buildEnrichedIndex(rows) {
    const byPath = new Map();
    const byBase = new Map(); // basename -> entry[]
    for (const row of rows || []) {
        const fp = row.filePath || row.file;
        if (!fp) continue;
        const t = row.track || row;
        // djpalId = Mixpedia catalog id — rides along so surfaces that fire a
        // like (or other catalog-keyed action) can send the real id instead of
        // an unresolvable artist/title text pair.
        const entry = { title: t.title || null, artist: t.artist || null, cover: t.cover || null, djpalId: t.djpalId || null };
        const np = normPath(fp);
        byPath.set(np, entry);
        // Basenames are indexed with fix-suffixes stripped, so an original and
        // its "[-9 LUFS]"/"[fixed]" copies all land in the same bucket.
        const base = stripFixTags(np.split('/').pop());
        if (base) {
            const list = byBase.get(base);
            if (list) list.push(entry); else byBase.set(base, [entry]);
        }
    }
    // Titles compare with fix-suffixes stripped too, so a bare DB row named
    // "X [-9 LUFS]" still counts as the same track as its enriched twin "X".
    const titleKey = (t) => stripFixTags(t).toLowerCase().replace(/[^a-z0-9]+/g, '');
    const sameTrack = (a, b) => {
        const ka = titleKey(a.title), kb = titleKey(b.title);
        return !ka || !kb || ka === kb;
    };
    // Resolve the one entry a basename may borrow enrichment from: every entry
    // sharing the name looks like the same track, and there is exactly one
    // distinct cover among them.
    const resolveBase = (base) => {
        const list = byBase.get(base);
        if (!list) return null;
        if (list.length === 1) return list[0];
        const covered = list.filter(e => e.cover);
        if (!covered.length) return null;
        if (!covered.every(e => e.cover === covered[0].cover)) return null;
        const rich = covered[0];
        return list.every(e => sameTrack(e, rich)) ? rich : null;
    };
    return {
        get(filePath) {
            const np = normPath(filePath);
            const base = stripFixTags(np.split('/').pop());
            // Fix-suffixes present in the ACTUAL filename (original casing) —
            // re-appended to the title so an original/copy pair never renders
            // with identical names (see PRESERVE_TAG_RE). Applies to exact-path
            // hits too: a derived copy's DB row inherits the ORIGINAL's title,
            // so without the tag "X [-9 LUFS].wav" rendered exactly like "X.wav".
            const fixTags = ((filePath || '').split(/[\\/]/).pop().replace(/\.[^/.]+$/, '')
                .match(new RegExp(FIX_TAG_SOURCE, 'gi')) || []);
            const withFixTags = (entry) => {
                if (!entry || !entry.title || !fixTags.length) return entry;
                // A "[x LUFS]" tag states the file's CURRENT loudness: when the
                // filename carries one, it REPLACES whatever LUFS tag the stored
                // (possibly borrowed from a twin) title has — never stacks
                // ("Da-Hool [-13 LUFS] [-16 LUFS]").
                let title = entry.title;
                if (fixTags.some((tag) => /lufs/i.test(tag))) {
                    title = title.replace(/\s*\[\s*-?\d+(?:\.\d+)?\s*lufs\s*\]/gi, '').trim();
                }
                // Only tags the title doesn't already carry.
                const missing = fixTags.filter((tag) => !title.toLowerCase().includes(tag.toLowerCase()));
                if (!missing.length && title === entry.title) return entry;
                return { ...entry, title: missing.length ? `${title} ${missing.join(' ')}` : title };
            };
            const pathHit = byPath.get(np);
            if (pathHit) {
                if (pathHit.cover) return withFixTags(pathHit);
                const twin = resolveBase(base);
                if (twin && twin.cover && sameTrack(pathHit, twin)) {
                    return withFixTags({
                        title: pathHit.title || twin.title,
                        artist: pathHit.artist || twin.artist,
                        cover: twin.cover,
                        djpalId: pathHit.djpalId || twin.djpalId || null,
                    });
                }
                return withFixTags(pathHit);
            }
            return withFixTags(resolveBase(base));
        },
    };
}

// Load the full local tracks table and index it by file path, so any tab
// (Compatibility, Loudness, …) can show the real track name/cover instead of
// the raw filename. The DB is the source of truth — it holds every analyzed
// track regardless of which tab last populated the in-memory Redux copy.
// `refreshKey` re-runs the query when it changes — pass a scan's isProcessing
// flag so covers a scan just fetched appear without remounting the tab.
// `loaded` says whether the DB has answered yet (false on the very first render):
// a caller that reads tags from disk for tracks the DB doesn't know must wait
// for it, or it parses the whole library before the answer arrives.
export function useEnrichedTracks(refreshKey) {
    const [index, setIndex] = useState(() => ({ ...buildEnrichedIndex([]), loaded: false }));
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const all = await window.electron.db.tracks.getAll();
                if (cancelled || !Array.isArray(all)) return;
                setIndex({ ...buildEnrichedIndex(all), loaded: true });
            } catch (e) {
                // DB unavailable — callers fall back to the filename
                if (!cancelled) setIndex({ ...buildEnrichedIndex([]), loaded: true });
            }
        })();
        return () => { cancelled = true; };
    }, [refreshKey]);
    return index;
}
